import { ResultOf } from "@graphql-typed-document-node/core";
import { Label } from "@radix-ui/react-label";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import gql from "graphql-tag";
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";

import { CopyToClipboardButton } from "@/components/CopyToClipboardButton";
import { TournamentBracketTree } from "@/components/TournamentBracketTree";
import { RoundCreateDialog } from "@/components/dialogs/RoundCreateDialog";
import { TournamentStageEditDialog } from "@/components/dialogs/TournamentStageEditDialog";
import {
    zFormEliminationSettings,
    zFormTrialFormat,
} from "@/components/forms/validation/tournament-stage";
import { DateTimeInput } from "@/components/inputs/DateTimeInput";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { FragmentType, getFragmentData, graphql } from "@/gen";
import { TieBreaker } from "@/gen/realtime";
import {
    SetAllRoundsAndGamesScheduledStartAtMutation,
    TournamentStageT,
} from "@/routes/tournament-stage/queries";
import { pluralize, printTieBreaker } from "@/routes/tournament-stage/utils";
import { extractChessresultsTournamentId } from "@/utils/chessresults";
import { isDefined } from "@/utils/types";

const FetchChessresultsPairingsMutation = gql`
    mutation FetchChessresultsPairings($input: FetchChessresultsPairingsInput!) {
        fetchChessresultsPairings(input: $input)
    }
`;

const BulkCreateRoundsMutation = gql`
    mutation BulkCreateRounds(
        $tournamentStageId: UUID!
        $startTime: DateTime!
        $numberOfRounds: Int!
    ) {
        bulkCreateRounds(
            tournamentStageId: $tournamentStageId
            startTime: $startTime
            numberOfRounds: $numberOfRounds
        )
    }
`;

const usePlayer = (playerId: string | null | undefined) => {
    const [result] = useQuery({
        query: graphql(`
            query tournamentStagePlayerQuery($id: UUID!) {
                player(id: $id) {
                    id
                    fideId
                    nickname
                }
            }
        `),
        variables: {
            id: playerId!,
        },
        pause: !playerId,
    });

    return {
        player: result.data?.player,
        loading: result.fetching,
        error: result.error,
    };
};

const PlayerDisplay = ({ playerId }: { playerId: string | null | undefined }) => {
    const { player, loading } = usePlayer(playerId);

    if (!playerId) return <span>—</span>;
    if (loading) return <span className="text-xs text-gray-500">Loading...</span>;
    if (!player) return <span className="text-xs text-gray-500">{playerId}</span>;

    return (
        <span>
            {player.nickname}
            {player.fideId && <span className="ml-1 text-xs text-gray-500">({player.fideId})</span>}
        </span>
    );
};

export default function TournamentStageRoute() {
    const { tournamentStage } = useOutletContext<{
        tournamentStage: TournamentStageT;
    }>();
    const [chessresultsTournamentId, setChessresultsTournamentId] = useState("");
    const [createRounds, setCreateRounds] = useState(false);
    const [_, fetchPairings] = useMutation(FetchChessresultsPairingsMutation);
    const [scheduledStartAt, setScheduledStartAt] = useState<Date | null>(null);
    const [startRound, setStartRound] = useState<number>(1);
    const [_setAllRoundsAndGamesResult, setAllRoundsAndGamesScheduledStartAt] = useMutation(
        SetAllRoundsAndGamesScheduledStartAtMutation
    );

    const [bulkCreateStartTime, setBulkCreateStartTime] = useState<Date | null>(null);
    const [numberOfRounds, setNumberOfRounds] = useState<number>(1);
    const [_bulkCreateResult, bulkCreateRounds] = useMutation(BulkCreateRoundsMutation);

    const handleBulkCreateRounds = async () => {
        if (!bulkCreateStartTime) {
            toast.error("Please select a start date and time.");
            return;
        }
        const confirmed = window.confirm(
            `This will create ${numberOfRounds} empty rounds starting from ${format(bulkCreateStartTime, "yyyy/MM/dd HH:mm")}. Are you sure?`
        );
        if (!confirmed) return;

        const res = await bulkCreateRounds({
            tournamentStageId: tournamentStage.id,
            startTime: bulkCreateStartTime.toISOString(),
            numberOfRounds: numberOfRounds,
        });

        if (res.error) {
            toast.error("Failed to create rounds", { description: res.error.message });
        } else {
            toast.success(`Successfully created ${numberOfRounds} rounds`);
        }
    };

    const handleFetchPairings = async () => {
        const extractedId = extractChessresultsTournamentId(chessresultsTournamentId);

        if (!extractedId) {
            toast.error("Enter a chessresults tournament ID or URL");
            return;
        }

        const variables = {
            input: {
                tournamentStageId: tournamentStage.id,
                chessresultsTournamentId: extractedId.trim(),
                createRounds: createRounds,
            },
        };

        const res = await fetchPairings(variables);
        if (res.error) {
            toast.error("Failed to import pairings", {
                description: res.error.message,
            });
        } else {
            toast.success("Pairings imported successfully");
        }
    };

    const handleUpdateRoundsAndGames = async () => {
        if (!scheduledStartAt) {
            toast.error("Please select a start date and time.");
            return;
        }
        const confirmed = window.confirm(
            "This will update the scheduled start time for all selected rounds and games in this tournament stage. Are you sure?"
        );
        if (!confirmed) return;

        const scheduledStartAtISO = scheduledStartAt.toISOString();

        const res = await setAllRoundsAndGamesScheduledStartAt({
            tournamentStageId: tournamentStage.id,
            scheduledStartAt: scheduledStartAtISO,
            startRound: startRound,
        });

        if (res.error) {
            toast.error("Failed to update start times", { description: res.error.message });
        } else {
            toast.success("Updated start times for selected rounds and games");
        }
    };

    const trialFormat = useMemo(
        () => zFormTrialFormat.safeParse(tournamentStage.trialFormat).data,
        [tournamentStage.trialFormat]
    );

    const tieBreakers: TieBreaker[] = (tournamentStage.tieBreakers as TieBreaker[]) ?? [];

    const eliminationSettings = useMemo(
        () => zFormEliminationSettings.safeParse(tournamentStage.eliminationSettings).data,
        [tournamentStage.eliminationSettings]
    );

    return (
        <Card className="bg-brand-white text-brand-black m-4 rounded border-none">
            <CardHeader>
                <CardTitle>{tournamentStage.title}</CardTitle>
                <div className="flex items-start justify-between">
                    <div className="flex flex-col">
                        <div className="flex-row items-center">
                            <span className="text-sm">ID: {tournamentStage.id}</span>
                            <CopyToClipboardButton text={tournamentStage.id} />
                        </div>
                    </div>
                    <TournamentStageEditDialog />
                </div>
            </CardHeader>
            <CardContent>
                <div className="flex flex-col gap-4">
                    <div className={"flex flex-col gap-4"}>
                        <div>
                            <Label className="text-md font-medium">Title</Label>
                            <p className="text-md text-muted-foreground">
                                {tournamentStage.title || "—"}
                            </p>
                        </div>
                        <div>
                            <Label className="text-md font-medium">Kind</Label>
                            <p className="text-md text-muted-foreground">
                                {tournamentStage.kind || "—"}
                            </p>
                        </div>
                        <div>
                            <Label className="text-md font-medium">Trial format</Label>
                            {trialFormat ? (
                                <ol className="list-inside list-decimal">
                                    {trialFormat.steps?.map((step, index) => (
                                        <li key={index}>
                                            {step.games
                                                ? "Fixed" in step.games
                                                    ? `${step.games.Fixed} ${pluralize(step.games.Fixed, "game", "games")}`
                                                    : `${step.games.RepeatUntilDecisive} ${pluralize(step.games.RepeatUntilDecisive, "game", "games")} at a time until there is a decisive result`
                                                : null}
                                            {step.point_system &&
                                                ` (${step.point_system.toLowerCase()})`}

                                            {index < (trialFormat.steps?.length ?? 0) - 1 && (
                                                <span className="text-brand-black text-sm">
                                                    , then in case of draw...
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ol>
                            ) : (
                                <p className="text-md text-muted-foreground">—</p>
                            )}
                        </div>
                        <div>
                            <Label className="text-md font-medium">Tie breakers</Label>
                            {tieBreakers ? (
                                <ol className="list-inside list-decimal">
                                    {tieBreakers?.map((tb, index) => (
                                        <li key={index}>{`${printTieBreaker(tb)}`}</li>
                                    ))}
                                </ol>
                            ) : (
                                <p className="text-md text-muted-foreground">—</p>
                            )}
                        </div>
                        <div>
                            <Label className="text-md font-medium">Order number</Label>
                            <p className="text-md text-muted-foreground">
                                {isDefined(tournamentStage.order) ? tournamentStage.order : "—"}
                            </p>
                        </div>
                        {tournamentStage.kind === "ELIMINATION" && (
                            <div>
                                <Label className="text-md font-medium">Elimination Settings</Label>
                                {eliminationSettings ? (
                                    <div className="space-y-2">
                                        <div>
                                            <span className="text-sm font-medium">Format: </span>
                                            <span className="text-muted-foreground text-sm">
                                                {eliminationSettings.format === "single"
                                                    ? "Single Elimination"
                                                    : "Double Elimination"}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium">
                                                Third place match:{" "}
                                            </span>
                                            <span className="text-muted-foreground text-sm">
                                                {eliminationSettings.include_third_place_match
                                                    ? "Yes"
                                                    : "No"}
                                            </span>
                                        </div>
                                        <InitialPairings
                                            pairings={eliminationSettings?.initial_pairings}
                                        />
                                    </div>
                                ) : (
                                    <p className="text-md text-muted-foreground">—</p>
                                )}
                            </div>
                        )}
                    </div>

                    <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="bulk-actions" className="bg-brand-white">
                            <AccordionTrigger className="text-lg">Bulk actions</AccordionTrigger>
                            <AccordionContent>
                                <div className="my-4 rounded border bg-gray-50 p-4">
                                    <Label className="mb-2 block text-lg font-semibold">
                                        Import rounds and pairings from Chessresults
                                    </Label>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            placeholder="Chessresults tournament ID or URL"
                                            value={chessresultsTournamentId}
                                            onChange={e =>
                                                setChessresultsTournamentId(e.target.value)
                                            }
                                            className="flex-1"
                                        />
                                        <div className="flex items-center space-x-2">
                                            <Label className="text-sm">Create rounds?</Label>
                                            <Checkbox
                                                checked={createRounds}
                                                onCheckedChange={checked =>
                                                    setCreateRounds(checked === true)
                                                }
                                            />
                                        </div>
                                        <Button onClick={handleFetchPairings}>
                                            Import rounds and pairings
                                        </Button>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="rounded border bg-gray-50 p-4">
                                        <Label className="mb-1 block text-lg font-semibold">
                                            Update start times for rounds and games
                                        </Label>
                                        <p className="text-muted-foreground mb-3 text-sm">
                                            Set the scheduled start time for the selected starting
                                            round below. Each subsequent round will be scheduled one
                                            day after the previous round, and all games in each
                                            round will be updated to match their round's new
                                            scheduled start time. You can select which round to
                                            start from below.
                                        </p>
                                        <div className="mt-2 flex flex-wrap items-end gap-2">
                                            <div className="mr-4 flex w-[180px] flex-col text-sm font-medium">
                                                <Label
                                                    htmlFor="start-round-select"
                                                    className="mb-1"
                                                >
                                                    Starting round
                                                </Label>
                                                <Select
                                                    value={String(startRound)}
                                                    onValueChange={(val: string) =>
                                                        setStartRound(Number(val))
                                                    }
                                                >
                                                    <SelectTrigger
                                                        id="start-round-select"
                                                        className="w-full"
                                                    >
                                                        <SelectValue placeholder="Select round" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {Array.isArray(tournamentStage.rounds) &&
                                                            getFragmentData &&
                                                            [
                                                                ...getFragmentData(
                                                                    RoundTable_Round,
                                                                    tournamentStage.rounds
                                                                ),
                                                            ]
                                                                .sort((a, b) =>
                                                                    a.title.localeCompare(b.title)
                                                                )
                                                                .map((round, idx) => (
                                                                    <SelectItem
                                                                        key={round.id}
                                                                        value={String(idx + 1)}
                                                                    >
                                                                        {round.title}
                                                                    </SelectItem>
                                                                ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <DateTimeInput
                                                value={scheduledStartAt}
                                                onValue={setScheduledStartAt}
                                                className="input"
                                            />
                                            <Button
                                                onClick={handleUpdateRoundsAndGames}
                                                variant="outline"
                                            >
                                                Update start times
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="rounded border bg-gray-50 p-4">
                                        <Label className="mb-1 block text-lg font-semibold">
                                            Create multiple rounds
                                        </Label>
                                        <p className="text-muted-foreground mb-3 text-sm">
                                            Create multiple empty rounds at once, scheduled on
                                            consecutive days.
                                        </p>
                                        <div className="flex flex-wrap items-end gap-2">
                                            <DateTimeInput
                                                value={bulkCreateStartTime}
                                                onValue={setBulkCreateStartTime}
                                                className="input"
                                            />
                                            <div className="mr-4 flex flex-col text-sm font-medium">
                                                <Label htmlFor="number-of-rounds" className="mb-1">
                                                    Number of rounds
                                                </Label>
                                                <Input
                                                    id="number-of-rounds"
                                                    type="number"
                                                    min="1"
                                                    value={numberOfRounds}
                                                    onChange={e =>
                                                        setNumberOfRounds(
                                                            Math.max(
                                                                1,
                                                                parseInt(e.target.value) || 1
                                                            )
                                                        )
                                                    }
                                                    className="h-10"
                                                />
                                            </div>
                                            <Button
                                                onClick={handleBulkCreateRounds}
                                                variant="outline"
                                            >
                                                Create rounds
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>

                    <RoundsTable roundsFragment={tournamentStage.rounds} />
                </div>
            </CardContent>
        </Card>
    );
}

const InitialPairings = ({
    pairings,
}: {
    pairings: {
        player1_id: string | null;
        player2_id: string | null;
    }[];
}) => {
    const validPairings = pairings ?? [];

    if (validPairings.length === 0) {
        return null;
    }

    if (validPairings.length > 8) {
        return (
            <div className="space-y-1">
                {validPairings.map(({ player1_id, player2_id }, idx) => (
                    <div key={idx} className="text-muted-foreground ml-4 text-sm">
                        {idx + 1}. <PlayerDisplay playerId={player1_id} /> vs{" "}
                        <PlayerDisplay playerId={player2_id} />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <>
            <div className="flex-1">
                <span className="text-sm font-medium">Initial bracket structure</span>
                <p className="text-muted-foreground mt-1 text-xs">
                    The bracket tree shows how the tournament will progress based on the pairing
                    order
                </p>
            </div>
            <TournamentBracketTree
                pairings={validPairings}
                playerDisplay={id => <PlayerDisplay playerId={id} />}
            />
        </>
    );
};

const RoundTable_Round = graphql(`
    fragment RoundTable_Round on Round {
        id
        title
        scheduledStartAt
        lastGameScheduledStartAt
        createdAt
    }
`);

const columns: ColumnDef<ResultOf<typeof RoundTable_Round>>[] = [
    {
        accessorKey: "title",
        header: () => <div>Title</div>,
        cell: ({ row }) => {
            return <div className="font-medium">{row.original.title}</div>;
        },
    },
    {
        accessorKey: "scheduledStartAt",
        header: () => <div>Scheduled start at</div>,
        cell: ({ row }) => {
            return (
                <div className="font-medium">
                    {format(row.original.scheduledStartAt, "yyyy/MM/dd HH:mm")}
                </div>
            );
        },
    },
    {
        accessorKey: "lastGameScheduledStartAt",
        header: () => <div>Last game scheduled start at</div>,
        cell: ({ row }) => {
            return (
                <div className="font-medium">
                    {row.original.lastGameScheduledStartAt
                        ? format(row.original.lastGameScheduledStartAt, "yyyy/MM/dd HH:mm")
                        : "-"}
                </div>
            );
        },
    },
];

const RoundsTable = ({
    roundsFragment,
}: {
    roundsFragment: FragmentType<typeof RoundTable_Round>[];
}) => {
    const navigate = useNavigate();
    const rounds = getFragmentData(RoundTable_Round, roundsFragment);

    return (
        <div>
            <DataTable
                columns={columns}
                data={rounds}
                onRowClick={row => {
                    navigate(`rounds/${row.id}`);
                }}
                title={"Rounds"}
                addButton={<RoundCreateDialog />}
            />
        </div>
    );
};
